import { useEffect, useState } from "react";

import { List, Text } from "react-native-paper";

import { useAppTheme } from "@hooks/useAppTheme";
import { Box } from "@memorang/ui";

import { AIFeedbackMenu } from "../../session/components/AIFeedbackMenu";
import { resolveHint } from "../helpers/hint";
import { useProethicsFlowStore } from "../hooks/useProethicsFlowStore";

const REVEAL_INTERVAL_MS = 16;
const REVEAL_CHARS_PER_TICK = 2;

const useRevealedText = (text: string, enabled: boolean) => {
	const [visibleLength, setVisibleLength] = useState(0);

	useEffect(() => {
		if (!enabled) {
			return;
		}
		setVisibleLength(0);
		const interval = setInterval(() => {
			setVisibleLength((length) => {
				if (length >= text.length) {
					clearInterval(interval);
					return length;
				}
				return length + REVEAL_CHARS_PER_TICK;
			});
		}, REVEAL_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [text, enabled]);

	return enabled ? text.slice(0, visibleLength) : text;
};

export const HintSuggestion = () => {
	const currentHint = useProethicsFlowStore((s) => s.currentHint);
	const hintPending = useProethicsFlowStore((s) => s.hintPending);
	const scenarioId = useProethicsFlowStore((s) => s.scenarioId);

	const hint = resolveHint(currentHint, scenarioId);
	if (hintPending || !hint) {
		return null;
	}

	return (
		<HintPill
			hint={hint}
			scenarioId={scenarioId}
			animate={currentHint !== null}
		/>
	);
};

const HintPill = ({
	hint,
	scenarioId,
	animate,
}: {
	hint: string;
	scenarioId: string;
	animate: boolean;
}) => {
	const theme = useAppTheme();
	const visibleHint = useRevealedText(hint, animate);

	return (
		<Box paddingHorizontal={12} paddingTop={8}>
			<List.Item
				left={(props) => (
					<List.Icon
						{...props}
						icon="lightbulb-on-outline"
						color={theme.colors.onPrimaryContainer}
						style={[
							props.style,
							{
								alignSelf: "center",
							},
						]}
					/>
				)}
				right={(props) => (
					<List.Icon
						{...props}
						style={[
							props.style,
							{
								alignSelf: "center",
								paddingRight: 8,
							},
						]}
						icon={() => (
							<AIFeedbackMenu itemId={scenarioId} featureName="AI Hint" />
						)}
					/>
				)}
				style={{
					borderRadius: theme.roundness * 3,
					backgroundColor: theme.colors.secondaryContainer,
					cursor: "auto",
					paddingVertical: 0,
					paddingRight: 4,
				}}
				title={() => (
					<Text
						variant="bodyMedium"
						accessibilityLiveRegion="polite"
						style={{
							color: theme.colors.onPrimaryContainer,
						}}
					>
						{visibleHint}
					</Text>
				)}
			/>
		</Box>
	);
};
